import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { z } from 'zod';
import { InstallmentStatusBadge } from '../components/badges';
import { ConfirmButton } from '../components/confirm';
import { Facts, Field, PageHeader } from '../components/page';
import { ActionError, Empty, QueryView } from '../components/states';
import { Badge } from '../components/ui/badge';
import { Button, buttonVariants } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input, Select, Textarea } from '../components/ui/input';
import { Table, TBody, TD, TH, THead, TR } from '../components/ui/table';
import { useAuth } from '../lib/auth';
import { day, instant, money, parseDinars } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { useApiMutation, useApiQuery } from '../lib/query';
import { useTenant } from '../lib/referentials';
import type { EscalationDetail, EscalationList, UserList } from '../lib/types';
import { CallCard } from './call-card';
import { Tabs } from './portfolio';

// File de travail (BA-039, spec 08 §5) : escalades à moi, à l'équipe, tri par ancienneté ou
// montant ; vue de traitement avec actions journalisées.

export const worklistSearchSchema = z.object({
  vue: z.enum(['calls', 'mine', 'team', 'all', 'closed']).optional(),
  tri: z.enum(['age', 'amount']).optional(),
});
type WorklistSearch = z.infer<typeof worklistSearchSchema>;

const KIND_TONE = {
  unpaid: 'danger',
  optout: 'neutral',
  payment_check: 'warning',
  question: 'info',
  overpayment: 'warning',
  refund: 'warning',
  delivery: 'danger',
  renewal: 'info',
  claim: 'warning',
} as const;

/** Compteur de la navigation : escalades ouvertes visibles, relu chaque minute. */
export function useOpenEscalations(): number {
  const query = useApiQuery<EscalationList>(
    '/v1/escalations',
    { limit: 1 },
    { refetchInterval: 60_000 },
  );
  return query.data?.counts.open ?? 0;
}

export function WorklistPage() {
  const { t, language } = useI18n();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as WorklistSearch;
  const view = search.vue ?? 'mine';
  const sort = search.tri ?? 'age';
  const query = useApiQuery<EscalationList>('/v1/escalations', {
    ...(view === 'closed' ? { status: 'closed' } : {}),
    // Appels du jour (BA-092) : rappels échus et dossiers jamais appelés.
    ...(view === 'calls' ? { calls: 'today' } : {}),
    ...(view === 'mine' || view === 'team' ? { assigned: view === 'mine' ? 'me' : 'team' } : {}),
    sort,
  });
  const go = (patch: Partial<WorklistSearch>) =>
    void navigate({ to: '/file-de-travail', search: { ...search, ...patch } });
  const counts = query.data?.counts;

  return (
    <>
      <PageHeader
        title={t.worklist.title}
        actions={
          <Select
            aria-label={t.common.filter}
            className="w-auto"
            value={sort}
            onChange={(e) => go({ tri: e.target.value as 'age' | 'amount' })}
          >
            <option value="age">{t.worklist.sortAge}</option>
            <option value="amount">{t.worklist.sortAmount}</option>
          </Select>
        }
      />
      <Tabs
        label={t.worklist.title}
        active={view}
        onChange={(key) => go({ vue: key })}
        tabs={[
          { key: 'calls', label: t.calls.tab },
          { key: 'mine', label: `${t.worklist.mine}${counts ? ` (${counts.mine})` : ''}` },
          { key: 'team', label: `${t.worklist.team}${counts ? ` (${counts.team})` : ''}` },
          { key: 'all', label: `${t.worklist.all}${counts ? ` (${counts.open})` : ''}` },
          { key: 'closed', label: t.worklist.closed },
        ]}
      />
      <Card>
        <QueryView query={query}>
          {(data) =>
            data.items.length === 0 ? (
              <Empty>{t.worklist.empty}</Empty>
            ) : (
              <Table>
                <THead>
                  <tr>
                    <TH>{t.worklist.kind}</TH>
                    <TH>{t.installments.policy}</TH>
                    <TH>{t.installments.policyholder}</TH>
                    <TH className="text-end">{t.worklist.remaining}</TH>
                    <TH>{t.worklist.openedAt}</TH>
                    <TH>{t.installments.status}</TH>
                  </tr>
                </THead>
                <TBody>
                  {data.items.map((e) => (
                    <TR key={e.id}>
                      <TD>
                        <Link
                          to="/file-de-travail/$id"
                          params={{ id: e.id }}
                          className="font-medium text-primary hover:underline"
                        >
                          {t.worklist.kinds[e.kind]}
                        </Link>
                        <p className="text-xs text-muted-foreground">{e.reason}</p>
                      </TD>
                      <TD className="whitespace-nowrap">{e.installment?.policyNumber ?? '-'}</TD>
                      <TD>{e.installment?.policyholderName ?? '-'}</TD>
                      <TD className="text-end whitespace-nowrap tabular-nums">
                        {e.installment
                          ? money(e.installment.amountDue - e.installment.amountPaid, language)
                          : '-'}
                      </TD>
                      <TD className="whitespace-nowrap">{day(e.openedAt, language)}</TD>
                      <TD>
                        <Badge tone={e.status === 'closed' ? 'neutral' : KIND_TONE[e.kind]}>
                          {t.worklist.statuses[e.status]}
                        </Badge>
                        {e.lastCallOutcome ? (
                          <p className="mt-1 text-xs text-muted-foreground">
                            {t.calls.outcomes[e.lastCallOutcome]} ({e.callAttempts})
                          </p>
                        ) : null}
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            )
          }
        </QueryView>
      </Card>
    </>
  );
}

function ActionForms({ escalation }: { escalation: EscalationDetail }) {
  const t = useI18n().t;
  const auth = useAuth();
  const navigate = useNavigate();
  const [note, setNote] = useState('');
  const [paid, setPaid] = useState({ method: 'agency', paidAt: '', amount: '' });
  const [days, setDays] = useState('7');
  const [resolution, setResolution] = useState('');
  const [assignee, setAssignee] = useState(escalation.assignedUserId ?? '');
  // Attribution changée par une autre action (prise en charge, appel) : le menu la suit.
  const [assigned, setAssigned] = useState(escalation.assignedUserId);
  if (assigned !== escalation.assignedUserId) {
    setAssigned(escalation.assignedUserId);
    setAssignee(escalation.assignedUserId ?? '');
  }
  const users = useApiQuery<UserList>(auth.can('administer') ? '/v1/users' : null);
  const act = useApiMutation<Record<string, unknown>, EscalationDetail>(
    'POST',
    `/v1/escalations/${escalation.id}/actions`,
    {
      invalidate: ['/v1/escalations', '/v1/installments', '/v1/dashboard'],
      onSuccess: (next) => {
        setNote('');
        if (next.status === 'closed') void navigate({ to: '/file-de-travail' });
      },
    },
  );
  const submit = (body: Record<string, unknown>) => (event?: FormEvent) => {
    event?.preventDefault();
    act.mutate(body);
  };
  const amount = paid.amount ? parseDinars(paid.amount) : undefined;
  const hasInstallment = escalation.installment !== null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.common.actions}</CardTitle>
        {escalation.status === 'open' ? (
          <Button size="sm" onClick={() => act.mutate({ action: 'take' })}>
            {t.worklist.take}
          </Button>
        ) : null}
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        <form onSubmit={submit({ action: 'note', note })} className="flex flex-col gap-2">
          <Field label={t.worklist.noteLabel}>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>
          <div className="flex gap-2">
            <Button
              type="submit"
              size="sm"
              variant="outline"
              disabled={!note.trim() || act.isPending}
            >
              {t.worklist.note}
            </Button>
          </div>
        </form>
        {hasInstallment ? (
          <form
            onSubmit={submit({
              action: 'mark_paid',
              method: paid.method,
              paidAt: paid.paidAt,
              ...(amount ? { amount } : {}),
            })}
            className="grid grid-cols-1 gap-2 sm:grid-cols-3"
          >
            <Field label={t.payments.method}>
              <Select
                value={paid.method}
                onChange={(e) => setPaid((p) => ({ ...p, method: e.target.value }))}
              >
                {(['agency', 'bank', 'check', 'other'] as const).map((m) => (
                  <option key={m} value={m}>
                    {t.paymentMethods[m]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t.payments.paidAt}>
              <Input
                type="date"
                value={paid.paidAt}
                onChange={(e) => setPaid((p) => ({ ...p, paidAt: e.target.value }))}
              />
            </Field>
            <Field label={t.payments.amount}>
              <Input
                inputMode="decimal"
                value={paid.amount}
                onChange={(e) => setPaid((p) => ({ ...p, amount: e.target.value }))}
              />
            </Field>
            <div className="sm:col-span-3">
              <Button type="submit" size="sm" disabled={!paid.paidAt || act.isPending}>
                {t.worklist.markPaid}
              </Button>
            </div>
          </form>
        ) : null}
        {hasInstallment ? (
          <form
            onSubmit={submit({ action: 'grant_delay', days: Number(days) })}
            className="flex items-end gap-2"
          >
            <Field label={t.worklist.days}>
              <Input
                type="number"
                min={1}
                max={90}
                className="w-24"
                value={days}
                onChange={(e) => setDays(e.target.value)}
              />
            </Field>
            <Button type="submit" size="sm" variant="outline" disabled={act.isPending}>
              {t.worklist.grantDelay}
            </Button>
          </form>
        ) : null}
        {auth.can('administer') && users.data ? (
          <div className="flex items-end gap-2">
            <Field label={t.worklist.assigned}>
              <Select value={assignee} onChange={(e) => setAssignee(e.target.value)}>
                <option value="">{t.worklist.nobody}</option>
                {users.data.items
                  .filter((u) => u.status === 'active')
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
              </Select>
            </Field>
            <Button
              size="sm"
              variant="outline"
              onClick={() => act.mutate({ action: 'assign', userId: assignee || null })}
            >
              {t.worklist.reassign}
            </Button>
          </div>
        ) : null}
        <form onSubmit={submit({ action: 'close', resolution })} className="flex items-end gap-2">
          <Field label={t.worklist.resolution} className="flex-1">
            <Input value={resolution} onChange={(e) => setResolution(e.target.value)} />
          </Field>
          <Button type="submit" size="sm" disabled={!resolution.trim() || act.isPending}>
            {t.worklist.close}
          </Button>
        </form>
        {hasInstallment && auth.can('editPortfolio') ? (
          <div>
            <ConfirmButton
              label={t.worklist.cancelContract}
              title={t.worklist.cancelContract}
              message={t.worklist.cancelConfirm}
              variant="destructive"
              onConfirm={() =>
                act.mutateAsync({
                  action: 'cancel_contract',
                  reason: resolution.trim() || t.worklist.cancelContract,
                })
              }
            />
          </div>
        ) : null}
        <ActionError error={act.error} />
      </CardContent>
    </Card>
  );
}

export function EscalationPage() {
  const { t, language } = useI18n();
  const { id } = useParams({ strict: false }) as { id: string };
  const tz = useTenant().data?.timezone;
  const query = useApiQuery<EscalationDetail>(`/v1/escalations/${id}`);
  return (
    <QueryView query={query}>
      {(e) => (
        <>
          <PageHeader
            title={`${t.worklist.kinds[e.kind]} : ${e.installment?.policyholderName ?? ''}`}
            subtitle={e.reason}
            actions={
              e.installment ? (
                <Link
                  to="/echeances/$id"
                  params={{ id: e.installment.id }}
                  className={buttonVariants({ variant: 'outline', size: 'sm' })}
                >
                  {t.installments.detailTitle} {e.installment.policyNumber}
                </Link>
              ) : null
            }
          />
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="flex flex-col gap-4">
              <Card>
                <CardContent>
                  <Facts
                    items={[
                      [t.installments.status, t.worklist.statuses[e.status]],
                      [t.worklist.openedAt, instant(e.openedAt, tz, language)],
                      ...(e.installment
                        ? ([
                            [t.installments.dueDate, day(e.installment.dueDate, language)],
                            [
                              t.worklist.remaining,
                              money(e.installment.amountDue - e.installment.amountPaid, language),
                            ],
                            [
                              t.installments.status,
                              <InstallmentStatusBadge key="s" status={e.installment.status} />,
                            ],
                          ] as [string, React.ReactNode][])
                        : []),
                      ...(e.detail
                        ? Object.entries(e.detail).map(
                            ([k, v]) => [k, String(v ?? '')] as [string, string],
                          )
                        : []),
                    ]}
                  />
                </CardContent>
              </Card>
              {e.inbound ? (
                <Card>
                  <CardHeader>
                    <CardTitle>{t.worklist.inbound}</CardTitle>
                    <span className="text-xs text-muted-foreground">
                      {instant(e.inbound.receivedAt, tz, language)}
                    </span>
                  </CardHeader>
                  <CardContent>
                    <p className="rounded-lg bg-muted p-3 text-sm whitespace-pre-wrap" dir="auto">
                      {e.inbound.body}
                    </p>
                    {e.inbound.suggestedIntent ? (
                      <p className="mt-3 text-sm text-muted-foreground" data-testid="suggestion">
                        {t.worklist.suggestion} :{' '}
                        <span className="font-medium text-foreground">
                          {t.worklist.intents[e.inbound.suggestedIntent]}
                        </span>
                        {e.inbound.suggestionConfidence !== null
                          ? ` (${e.inbound.suggestionConfidence} %)`
                          : null}
                      </p>
                    ) : null}
                  </CardContent>
                </Card>
              ) : null}
              <Card>
                <CardHeader>
                  <CardTitle>{t.worklist.messages}</CardTitle>
                </CardHeader>
                {e.messages.length === 0 ? (
                  <Empty />
                ) : (
                  <ul className="divide-y divide-border">
                    {e.messages.map((m) => (
                      <li key={m.id} className="flex justify-between gap-2 px-4 py-2 text-sm">
                        <span>
                          {t.channels[m.channel]} : {m.status}
                        </span>
                        <span className="text-muted-foreground">
                          {instant(m.sentAt, tz, language)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            </div>
            <div className="flex flex-col gap-4">
              {e.status !== 'closed' && e.installment ? <CallCard escalation={e} /> : null}
              {e.status !== 'closed' ? <ActionForms escalation={e} /> : null}
              <Card>
                <CardHeader>
                  <CardTitle>{t.worklist.notes}</CardTitle>
                </CardHeader>
                {e.notes.length === 0 ? (
                  <Empty />
                ) : (
                  <ol className="divide-y divide-border">
                    {e.notes.map((n) => (
                      <li key={n.id} className="px-4 py-2 text-sm">
                        <span className="font-medium">
                          {t.worklist.actions[n.action] ?? n.action}
                        </span>
                        {n.body ? <span> : {n.body}</span> : null}
                        <p className="text-xs text-muted-foreground">
                          {instant(n.at, tz, language)}
                        </p>
                      </li>
                    ))}
                  </ol>
                )}
              </Card>
            </div>
          </div>
        </>
      )}
    </QueryView>
  );
}
